import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChecklistFieldMode, ChecklistItemKind, IChecklistItem } from '@interfaces/IScheduler';
import { Button } from '@components/ui/Button/Button';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { EVIDENCE_KINDS } from '@d-orca/shared/checklist';
import styles from './ChecklistAuthoring.module.css';

const MODES: readonly ChecklistFieldMode[] = ['none', 'optional', 'required'];

interface IChecklistItemFormProps {
  initial: IChecklistItem;
  onSave: (item: IChecklistItem) => void;
  onCancel: () => void;
}

/** One checklist item's editor: its kind, its words, and — for a check — the
 *  evidence each tick must carry. */
export function ChecklistItemForm({ initial, onSave, onCancel }: IChecklistItemFormProps) {
  const { t } = useTranslation();
  const [item, setItem] = useState(initial);
  const kinds = (['check', 'description'] as const).map((id) => ({ id, label: t(`scheduler.panel.checklist.itemKind.${id}`) }));
  const modes = MODES.map((id) => ({ id, label: t(`scheduler.panel.checklist.mode.${id}`) }));

  return (
    <div className={styles.editor}>
      <SegmentedControl<ChecklistItemKind>
        ariaLabel={t('scheduler.panel.checklist.fields.kind')}
        value={item.kind}
        options={kinds}
        onChange={(kind) => setItem({ ...item, kind })}
        variant="joined"
      />
      <label className={styles.field}>
        {t('scheduler.panel.checklist.fields.content')}
        <input
          type="text"
          aria-label={t('scheduler.panel.checklist.fields.content')}
          value={item.content}
          maxLength={500}
          placeholder={t(`scheduler.panel.checklist.contentPlaceholder.${item.kind}`)}
          onChange={(e) => setItem({ ...item, content: e.target.value })}
          autoFocus
        />
      </label>
      {item.kind === 'check' && (
        <div className={styles.evidenceGrid}>
          <span className={styles.segLabel}>{t('scheduler.panel.checklist.evidenceTitle')}</span>
          {EVIDENCE_KINDS.map((field) => (
            <div key={field} className={styles.segRow}>
              <span className={styles.segLabel}>{t(`scheduler.panel.checklist.fields.${field}`)}</span>
              <SegmentedControl<ChecklistFieldMode>
                ariaLabel={t(`scheduler.panel.checklist.fields.${field}`)}
                value={item[field]}
                options={modes}
                onChange={(mode) => setItem({ ...item, [field]: mode })}
                variant="joined"
              />
            </div>
          ))}
        </div>
      )}
      <div className={styles.editorFooter}>
        <Button variant="ghost" size="sm" onClick={onCancel}>{t('scheduler.panel.checklist.editorCancel')}</Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!item.content.trim()}
          onClick={() => onSave({ ...item, content: item.content.trim() })}
        >
          {t('scheduler.panel.checklist.editorSave')}
        </Button>
      </div>
    </div>
  );
}
